/**
 * Lives in `lib/` since 22.09.2026 — the register and the record read it
 * too, and features may not import each other (R-W1).
 */
import type { CaseStatus } from "@/api";

/** The three stages a person sets by hand (design 2026-09-07 §5.2). */
export type ManualStage = "documents" | "sent" | "outcome";

/** Which screen a row opens (design 2026-09-13 §9). */
export type Entry = "planning" | "calculation";

export interface Stage {
  word: string;
  percent: number;
  /** blocked / change_required / special_case — shown, not clickable. */
  resting: boolean;
  /** rejected / cancelled / archived — hidden behind „beendete zeigen". */
  finished: boolean;
  /** The one tick that may be clicked now, or nothing. */
  next: ManualStage | null;
  done: ManualStage[];
  /** The board, or the calculation — where a click on the row goes. */
  entry: Entry;
  /**
   * A second word after the stage — „Freigabe offen", „Preis fehlt" — or
   * nothing. `extraWords` may say something more precise in its place (the
   * gaps of a calculation that has an amount, UX gate round 4, #2; a reason
   * that is not a price, „Maß fehlt", S-11).
   */
  note: string | null;
}

export const NEXT_TARGET: Record<Exclude<ManualStage, "outcome">, CaseStatus> = {
  documents: "documents_captured",
  sent: "sent",
};

type Row = Omit<Stage, "done">;

function planning(word: string, percent: number, next: ManualStage | null = null): Row {
  return { word, percent, resting: false, finished: false, next, entry: "planning", note: null };
}

/** „In Kalkulation" at 80 %: the stage the engine and the approval move a case through (D-11). */
function calculation(note: string | null, next: ManualStage | null = null): Row {
  return { word: "In Kalkulation", percent: 80, resting: false, finished: false, next, entry: "calculation", note };
}

const RESTING: Row = { word: "ruht", percent: 50, resting: true, finished: false, next: null, entry: "planning", note: null };

const TABLE: Record<CaseStatus, Row> = {
  new: planning("Neu", 0, "documents"),
  documents_captured: planning("Unterlagen da", 20),
  extraction_review: planning("Unterlagen da", 20),
  question_open: planning("Unterlagen da", 20),
  extraction_failed: planning("Unterlagen da", 20),
  planning_ready: planning("In Planung", 50),
  // E-52: the quote leaves only after an approved calculation — no tick here.
  planning_in_progress: planning("In Planung", 50),
  technical_review: planning("In Planung", 50),
  // A calculation waiting for the one signature that makes it an offer
  // (E-52) — the rows the greeting points at („… wartet auf deine
  // Freigabe") say so under the chip (UX gate round 2, #1). „Preis fehlt"
  // below keeps its own, more precise word.
  calculation_ready: calculation("Freigabe offen"),
  commercial_review: calculation("Freigabe offen"),
  price_data_missing: calculation("Preis fehlt"),
  jtl_ready: calculation(null),
  jtl_transferred: calculation(null),
  // The one place „Angebot raus" may be ticked (D-11, D-12). Its own word
  // (UX gate round 4, #2): „In Kalkulation" with „freigegeben" under it stood
  // beside a greeting that says „ist freigegeben — das Angebot kann raus" —
  // the chip said the stage before the one the Vorgang is in. Still the
  // calculation's stage for the path (80 %, entry „calculation").
  quote_approved: {
    word: "Freigegeben",
    percent: 80,
    resting: false,
    finished: false,
    next: "sent",
    entry: "calculation",
    note: null,
  },
  sent: { word: "Angebot raus", percent: 100, resting: false, finished: false, next: "outcome", entry: "calculation", note: null },
  won: { word: "Gewonnen", percent: 100, resting: false, finished: false, next: null, entry: "calculation", note: null },
  lost: { word: "Verloren", percent: 100, resting: false, finished: false, next: null, entry: "calculation", note: null },
  blocked: RESTING,
  change_required: RESTING,
  special_case: RESTING,
  rejected: { word: "Abgewiesen", percent: 0, resting: false, finished: true, next: null, entry: "planning", note: null },
  cancelled: { word: "Storniert", percent: 0, resting: false, finished: true, next: null, entry: "planning", note: null },
  archived: { word: "Archiviert", percent: 100, resting: false, finished: true, next: null, entry: "calculation", note: null },
};

function doneFor(percent: number, status: CaseStatus): ManualStage[] {
  const done: ManualStage[] = [];
  if (percent >= 20) done.push("documents");
  if (percent >= 100 && status !== "archived") done.push("sent");
  if (status === "won" || status === "lost") done.push("outcome");
  return done;
}

/**
 * The stage a status stands for.
 *
 * `TABLE` is `Record<CaseStatus, …>`, so a status missing from it is a
 * compile error. The runtime check is not redundant for all that: the
 * browser is a **deployed** artefact. The day the backend learns a
 * twenty-fourth status, this bundle is still the one built against
 * twenty-three — `TABLE[status]` is `undefined` while the type says it
 * cannot be, and `row.percent` would throw the whole list off the screen.
 */
export function stageOf(status: CaseStatus): Stage {
  if (!Object.hasOwn(TABLE, status)) {
    return { word: status, percent: 0, resting: true, finished: false, next: null, done: [], entry: "planning", note: null };
  }
  const row = TABLE[status];
  return { ...row, done: doneFor(row.percent, status) };
}

/** Every status the contract knows, in the order of the table above. */
export const ALL_STATUSES = Object.keys(TABLE) as CaseStatus[];

/**
 * How a stage reads as one phrase — the start list's own wording (a resting
 * case leads with „In Planung", design 2026-09-07 §5.1). No percent: the
 * word is the stage (CI §6).
 */
export function stageText(stage: Stage): string {
  return stage.resting ? "In Planung · ruht" : stage.word;
}

export interface StageFilter {
  value: string;
  label: string;
  statuses: readonly CaseStatus[];
}

/**
 * The register's „Stadium" filter (design 2026-09-22 §8.1): one entry per
 * word a row can show, each carrying the statuses behind that word — the
 * server filters by status (`?status=…`, repeated), the person by stage.
 * A test holds both directions: every status in exactly one filter, every
 * filter named by the word its rows show.
 */
export const STAGE_FILTERS: readonly StageFilter[] = [
  { value: "new", label: "Neu", statuses: ["new"] },
  {
    value: "documents",
    label: "Unterlagen da",
    statuses: ["documents_captured", "extraction_review", "question_open", "extraction_failed"],
  },
  { value: "planning", label: "In Planung", statuses: ["planning_ready", "planning_in_progress", "technical_review"] },
  {
    value: "calculation",
    label: "In Kalkulation",
    statuses: ["calculation_ready", "commercial_review", "price_data_missing", "jtl_ready", "jtl_transferred"],
  },
  // A filter of its own, since the chip says it (UX gate round 4, #2): the
  // test below holds every filter to the word its rows show.
  { value: "approved", label: "Freigegeben", statuses: ["quote_approved"] },
  { value: "sent", label: "Angebot raus", statuses: ["sent"] },
  { value: "won", label: "Gewonnen", statuses: ["won"] },
  { value: "lost", label: "Verloren", statuses: ["lost"] },
  { value: "resting", label: "Ruht", statuses: ["blocked", "change_required", "special_case"] },
  { value: "finished", label: "Beendet", statuses: ["rejected", "cancelled", "archived"] },
];

/**
 * The stages in which a Vorgang can no longer be handed over (D-4, contract
 * §3.2 `FINISHED_STAGES`). Not the same set as `stage.finished` — won and
 * lost stay visible in every list, but nobody hands over a closed deal.
 */
export const HANDOVER_CLOSED: ReadonlySet<CaseStatus> = new Set<CaseStatus>([
  "won",
  "lost",
  "rejected",
  "cancelled",
  "archived",
]);

/**
 * Whether „Vorgang stornieren …" is offered (spec 2026-10-15 §5): in every
 * stage before the quote leaves. Not from `sent` on — „Angebot raus" is
 * answered with Gewonnen or Verloren, not with a cancellation — and not in the
 * finished stages, which includes `cancelled` itself (nothing is cancelled
 * twice). The server's `TRANSITIONS` is the judge; this only keeps the
 * button off a Vorgang that could never take it.
 */
export function canCancel(status: CaseStatus): boolean {
  return status !== "sent" && !HANDOVER_CLOSED.has(status);
}

/**
 * The statuses an „N offene Vorgänge" count means (register fix round 1,
 * I3): every `STAGE_FILTERS` entry except „won", „lost" and „finished" — the
 * overview endpoint's own default already drops the three finished statuses,
 * but keeps `won`/`lost`, so a register asking for „offen" without this list
 * would count a decided Vorgang as open. Derived from `STAGE_FILTERS` so the
 * two can never drift apart.
 */
export const OPEN_STATUSES: readonly CaseStatus[] = STAGE_FILTERS.filter(
  (filter) => filter.value !== "won" && filter.value !== "lost" && filter.value !== "finished",
).flatMap((filter) => filter.statuses);
